import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { parse as parseQuery } from 'query-string';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import filesAPI from '../api/files';
import mentorsAPI, {
  Mentor,
  MentorDetails,
  MentorId,
  MentorStatus,
} from '../api/mentors';
import venturesAPI from '../api/ventures';
import { Venture } from '../api/ventures/types/Venture';
import { SnackMessage } from '../components/common';
import { massAsyncRequest } from '../utils/api';
import { isMobile } from '../utils/functions';

export interface MentorsContextProps {
  resetKey: number;
  mentors: Mentor[];
  mentorsLogos: { [x: string]: string | null };
  selectedMentorIds: Mentor['id'][];
  assignTargetMentor: Mentor | null;
  archiveTargetMentors: Mentor[] | null;
  inviteTargetMentorId: MentorId | null;
  profileUpdateTargetMentorId: MentorId | null;
  loadedVenturesById: {
    [x: string]: Venture[];
  };
  isAssigningMentor: { [x: string]: boolean };
  isSendingProfileUpdate: boolean;
  isChangingMentorStatus: boolean;
  setMentors: Dispatch<SetStateAction<Mentor[]>>;
  setSelectedMentorIds: Dispatch<SetStateAction<Mentor['id'][]>>;
  setIsSendingProfileUpdate: Dispatch<SetStateAction<boolean>>;
  setInviteTargetMentorId: Dispatch<SetStateAction<MentorId | null>>;
  handleAssingSuccessForm: (
    mentorId: Mentor['id'],
    handleClose: () => void,
  ) => (data: {
    assigned: string[];
    removed: string[];
    all: Venture[];
  }) => Promise<void>;
  handleVenturesLoaded: (
    mentorId: Mentor['id'],
  ) => (loadedVentures: Venture[]) => void;
  setProfileUpdateTargetMentorId: (mentorId: MentorId | null) => void;
  setAssignTargetMentor: (mentor: Mentor | null) => void;
  setArchiveTargetMentors: Dispatch<SetStateAction<Mentor[] | null>>;
  sendMentorUpdateProfilesTarget: (onClose: () => {}) => Promise<void>;
  sendMentorUpdateProfilesSelected: (onClose: () => {}) => Promise<void>;
  changeMentorStatus: (status: MentorStatus) => Promise<void>;
}

interface MentorsProviderProps {
  children: ReactNode;
}

export const MentorsContext = createContext<MentorsContextProps>({
  resetKey: 0,
  mentors: [],
  mentorsLogos: {},
  selectedMentorIds: [],
  assignTargetMentor: null,
  archiveTargetMentors: null,
  inviteTargetMentorId: null,
  profileUpdateTargetMentorId: null,
  loadedVenturesById: {},
  isAssigningMentor: {},
  isSendingProfileUpdate: false,
  isChangingMentorStatus: false,
  setMentors: () => {},
  setSelectedMentorIds: () => {},
  setIsSendingProfileUpdate: () => {},
  setInviteTargetMentorId: () => {},
  handleAssingSuccessForm:
    () =>
    // eslint-disable-next-line no-empty-pattern
    async ({ assigned: [], removed: [], all: [] }) => {},
  handleVenturesLoaded: () => () => {},
  setProfileUpdateTargetMentorId: () => {},
  setAssignTargetMentor: () => {},
  setArchiveTargetMentors: () => {},
  sendMentorUpdateProfilesTarget: async () => {},
  sendMentorUpdateProfilesSelected: async () => {},
  changeMentorStatus: async () => {},
});

const validateMentor = (mentor: Mentor): MentorDetails => {
  return {
    address: mentor.address,
    firstName: mentor.firstName,
    lastName: mentor.lastName,
    about: mentor.about,
    dateOfBirth: mentor.dateOfBirth,
    dateOfEnrollment: mentor.dateOfEnrollment,
    email: mentor.email,
    phone: mentor.phone,
    linkedInProfile: mentor.linkedInProfile,
    country: mentor.country,
    state: mentor.state,
    city: mentor.city,
    logo: mentor.logo,
    status: mentor.status,
    zip: mentor.zip,
    currentRole: mentor.currentRole,
    companyName: mentor.companyName,
    seniority: mentor.seniority,
    experienceYears: mentor.experienceYears,
    isEnterpreneur: mentor.isEnterpreneur,
    isInvestor: mentor.isInvestor,
    skills: mentor.skills,
    mentorCapacity: mentor.mentorCapacity,
  };
};

export const MentorsProvider = ({ children }: MentorsProviderProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();

  const searchParams = useMemo(
    () => parseQuery(location.search),
    [location.search],
  );

  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [mentorsLogos, setMentorsLogos] = useState<{
    [x: string]: string | null;
  }>({});
  const [assignTargetMentor, setAssignTargetMentor] = useState<Mentor | null>(
    null,
  );
  const [isAssigningMentor, setIsAssigningMentor] = useState<{
    [x: string]: boolean;
  }>({});
  const [profileUpdateTargetMentorId, setProfileUpdateTargetMentorId] =
    useState<MentorId | null>(null);
  const [inviteTargetMentorId, setInviteTargetMentorId] =
    useState<MentorId | null>(null);
  const [loadedVenturesById, setLoadedVenturesById] = useState<{
    [x: string]: Venture[];
  }>({});
  const [selectedMentorIds, setSelectedMentorIds] = useState<Mentor['id'][]>(
    [],
  );
  const [archiveTargetMentors, setArchiveTargetMentors] = useState<
    Mentor[] | null
  >(null);
  const [isSendingProfileUpdate, setIsSendingProfileUpdate] = useState(false);
  const [isChangingMentorStatus, setIsChangingMentorStatus] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const reset = useCallback(() => setResetKey((prev) => prev + 1), []);

  const handleVenturesLoaded = useCallback(
    (mentorId: Mentor['id']) => (loadedVentures: Venture[]) => {
      setMentors((prevMentors) =>
        prevMentors.map((mentor) =>
          mentor.id === mentorId
            ? { ...mentor, assigned: loadedVentures.length }
            : mentor,
        ),
      );
      setLoadedVenturesById((prevState) => ({
        ...prevState,
        [mentorId]: loadedVentures,
      }));
    },
    [],
  );

  const handleAssingSuccessForm = useCallback(
    (mentorId: Mentor['id'], handleModalClose: () => any) =>
      async ({
        assigned,
        removed,
        all,
      }: {
        assigned: string[];
        removed: string[];
        all: Venture[];
      }) => {
        if (!assigned.length && !removed.length) {
          return handleModalClose();
        }

        try {
          setIsAssigningMentor((prev) => ({ ...prev, [mentorId]: true }));
          const assignedRequests = assigned.map(
            (ventureId) => () => venturesAPI.assignMentor(ventureId, mentorId),
          );
          const removedRequests = removed.map(
            (ventureId) => () =>
              venturesAPI.removeMentorAssignment(ventureId, mentorId),
          );
          await Promise.all([
            massAsyncRequest(assignedRequests),
            massAsyncRequest(removedRequests),
          ]);
          handleVenturesLoaded(mentorId)(all);
          handleModalClose();
          setIsAssigningMentor((prev) => ({ ...prev, [mentorId]: false }));
        } catch (e: any) {
          console.log('error', 'e');
        }
      },
    [handleVenturesLoaded],
  );

  const handleSendMentorUpdateProfiles = useCallback(
    async (mentorIds: MentorId[], onClose: () => {}) => {
      try {
        setIsSendingProfileUpdate(true);
        await mentorsAPI.sendMentorUpdateInvites(mentorIds);
        enqueueSnackbar('The profile update requests were successfully sent', {
          variant: 'success',
        });
        setIsSendingProfileUpdate(false);
        onClose();
      } catch (e) {
        const messageError = (e as AxiosError).response?.data?.message;

        setIsSendingProfileUpdate(false);
        enqueueSnackbar(
          'An error occurred while sending profile update request. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        throw e;
      }
    },
    [enqueueSnackbar],
  );

  const sendMentorUpdateProfilesTarget = useCallback(
    async (onClose: () => {}) => {
      try {
        if (!profileUpdateTargetMentorId) {
          return;
        }

        await handleSendMentorUpdateProfiles(
          [profileUpdateTargetMentorId],
          onClose,
        );

        setProfileUpdateTargetMentorId(null);
      } catch (e) {
        console.error('Error sending mentor update profiles:', e);
      }
    },
    [handleSendMentorUpdateProfiles, profileUpdateTargetMentorId],
  );

  const sendMentorUpdateProfilesSelected = useCallback(
    async (onClose: () => {}) => {
      try {
        if (!selectedMentorIds.length) {
          return;
        }

        await handleSendMentorUpdateProfiles(selectedMentorIds, onClose);

        setSelectedMentorIds([]);
      } catch (e) {
        console.error('Error sending mentor update profiles:', e);
      }
    },
    [handleSendMentorUpdateProfiles, selectedMentorIds],
  );

  const loadMentorsLogos = useCallback(async (mentorsList: Mentor[]) => {
    try {
      const mentorImages = mentorsList.map((mentor: Mentor) => ({
        id: mentor.id,
        logo: mentor.logo,
      }));
      setMentorsLogos(() => {
        const sources = mentorImages.reduce((res, mentor) => {
          res[mentor.id] = mentor.logo ? '' : null;
          return res;
        }, {} as { [x: string]: string | null });
        return sources;
      });
      const shouldBeLoaded = mentorImages.filter(
        (mentorImage) => !!mentorImage.logo,
      );
      const logos = shouldBeLoaded.map((mentorImage) => mentorImage.logo);
      if (!logos.length) {
        return false;
      }
      const loadedLogos = await filesAPI.getFileLogos(logos as string[]);

      const nextState = shouldBeLoaded.reduce(
        (res, mentorImage, mentorImageIndex) => {
          res[mentorImage.id] = loadedLogos[mentorImageIndex];
          return res;
        },
        {} as { [x: string]: string },
      );
      return setMentorsLogos((prevMentors) => ({
        ...prevMentors,
        ...nextState,
      }));
    } catch (e: any) {
      console.log('loadMentorsLogos error');
      return false;
    }
  }, []);

  const changeMentorStatus = useCallback(
    async (status: MentorStatus) => {
      try {
        if (!archiveTargetMentors?.length) return;
        setIsChangingMentorStatus(true);
        const changeStatus = archiveTargetMentors.map((value: Mentor) => {
          return () =>
            mentorsAPI.update(value.id, {
              ...validateMentor(value),
              status,
            });
        });
        const res = await massAsyncRequest(changeStatus);

        if (res.some((value) => value === undefined)) {
          throw new Error('An error occurred while changing mentor status.');
        }
      } catch (e: any) {
        console.error(e);
        enqueueSnackbar(
          'An error occurred while changing mentor status. Please, try again.',
          { variant: 'error' },
        );
      } finally {
        // reset to force reload
        if (isMobile()) {
          if (!searchParams?.search) {
            setMentors((prev) =>
              prev.filter((mentor) =>
                archiveTargetMentors?.some((tf) => tf.id !== mentor.id),
              ),
            );
          } else {
            setMentors((prev) =>
              prev.map((mentor) =>
                archiveTargetMentors?.some((tf) => tf.id === mentor.id)
                  ? { ...mentor, status }
                  : mentor,
              ),
            );
          }
        } else {
          reset();
        }

        setArchiveTargetMentors(null);
        setIsChangingMentorStatus(false);
      }
    },
    [archiveTargetMentors, enqueueSnackbar, reset, searchParams],
  );

  useEffect(() => {
    if (mentors.length) {
      loadMentorsLogos(mentors);
    }
  }, [loadMentorsLogos, mentors]);

  const contextValue: MentorsContextProps = {
    resetKey,
    mentors,
    mentorsLogos,
    selectedMentorIds,
    assignTargetMentor,
    archiveTargetMentors,
    inviteTargetMentorId,
    profileUpdateTargetMentorId,
    loadedVenturesById,
    isAssigningMentor,
    isSendingProfileUpdate,
    isChangingMentorStatus,
    setMentors,
    setSelectedMentorIds,
    setIsSendingProfileUpdate,
    setInviteTargetMentorId,
    handleAssingSuccessForm,
    handleVenturesLoaded,
    setProfileUpdateTargetMentorId,
    setAssignTargetMentor,
    setArchiveTargetMentors,
    sendMentorUpdateProfilesTarget,
    sendMentorUpdateProfilesSelected,
    changeMentorStatus,
  };

  return (
    <MentorsContext.Provider value={contextValue}>
      {children}
    </MentorsContext.Provider>
  );
};

export const useMentorsContext = () => {
  const context = useContext(MentorsContext);
  if (!context) {
    throw new Error('useMentorsContext must be used within a MentorsProvider');
  }
  return context;
};
